import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Grid,
    TextField,
    List,
    ListItem,
    ListItemText,
    Typography,
    CircularProgress
} from "@mui/material/";
import { strings } from "../../../../common/i18n";
import debounce from "lodash/debounce";
import actions from "../../../../Store/actions";
import makeStyles from "@mui/styles/makeStyles";
import styles from "./styles";
import DownloadAsset from "./DownloadAsset";

/**
 * Asset Audit Component
 */
const debounceMs = 300;
export default ({ isDisabled }) => {
    const { assets, auditsCreate, userOrganisations } = useSelector(
        (state) => state
    );

    const [vinValue, setVinValues] = useState("");
    const [loading, setLoading] = useState(true);

    const [validAssetTypes, setValidAssetTypes] = useState({
        total: "",
        known: "",
        unknown: ""
    });
    const isMountedRef = useRef(true);
    const classes = makeStyles(styles)();
    const dispatch = useDispatch();

    useEffect(() => {
        isMountedRef.current = true;
        return () => { isMountedRef.current = false; };
    }, []);

    /** Debounce for searching  */
    const debouncedSearch = debounce(async (value) => {
        let vinList = [];

        /** split value based on the new line remove empty line if present  */
        const list = value.split(`\n`).filter((item) => item.length > 0);

        // Build the identifier1 -> asset lookup once per paste instead of
        // re-scanning the whole asset list per pasted line — that was
        // lines x assets of synchronous work with no cap, freezing the tab
        // for ~20s on a 5,000-line paste.
        // Keep the first row per identifier, but OR `isExpected` across every
        // duplicate. The old nested `.find` callback never returned true, so
        // it scanned the whole list and set isExpected if *any* row with that
        // VIN was expected. Taking only the first match would quietly change
        // the expected-asset count whenever a VIN appears twice.
        const assetByIdentifier = new Map();
        assets.data.forEach((item) => {
            const existing = assetByIdentifier.get(item.asset.identifier1);
            if (!existing) {
                assetByIdentifier.set(item.asset.identifier1, {
                    isExpected: Boolean(item.isExpected)
                });
            } else if (item.isExpected) {
                existing.isExpected = true;
            }
        });

        let validVin = 0;
        let expectedAssetCount = 0;
        list.forEach((item) => {
            const { isValidVin, isExpected } = vinCheck(item, assetByIdentifier);
            if (isValidVin) validVin++;
            if (isExpected) expectedAssetCount++;
            vinList.push(item);
        });

        await dispatch(
            actions.auditsCreate.create.itemSet({
                filters: {
                    assetsToAudit: vinList
                },
                assetsToAudit: strings("messages.assetsToAudit", {
                    count: expectedAssetCount
                })
            })
        );

        if (isMountedRef.current) {
            setValidAssetTypes({
                total: vinList.length,
                known: validVin,
                unknown: vinList.length - validVin
            });
        }
    }, debounceMs);

    const handleVinChange = (event) => {
        const { value } = event.target;
        setVinValues(value);
        debouncedSearch(value);
    };

    /** Check vin in the existing assets data */
    const vinCheck = (vin, assetByIdentifier) => {
        let next = {
            isValidVin: false,
            isExpected: false
        };
        const match = assetByIdentifier.get(vin);
        if (match) {
            next.isValidVin = true;
            if (match.isExpected) next.isExpected = true;
        }
        return next;
    };

    /* 1. Get selectedBranches, selectedLocations and data from auditCreate
     * 2. Get current org from the userOrganisations
     *  everything will be present for this step */
    const { selectedBranches, selectedLocations, data } = auditsCreate;
    const orgId = userOrganisations.item.id;

    useEffect(() => {
        let isMounted = true;
        (async () => {
            const branchIds = selectedBranches.map(
                (id) => data.find((el) => el.id === id).branch
            );
            const next = {
                organisation: orgId,
                branch: branchIds,
                location: selectedLocations.ids,
                is_live: 1
            };
            await dispatch(actions.assets.create.getSelectedAssetRequest(next));
            if (isMounted) setLoading(false);
        })();
        return () => { isMounted = false; };
    }, []);

    return (
        <>
            {loading ? (
                <Typography
                    component="div"
                    className={classes.loadingContainer}
                >
                    <CircularProgress />
                    <Typography component="div">
                        {strings("loading")}...
                    </Typography>
                </Typography>
            ) : (
                <Grid container>
                    <Grid item xs={12}>
                        <Typography component="div">
                            <Typography component="div">
                                * {strings("messages.vinMessage")}
                            </Typography>
                        </Typography>
                        <TextField
                            className={classes.spacer}
                            fullWidth
                            disabled={isDisabled}
                            multiline
                            onChange={handleVinChange}
                            placeholder={`${strings("messages.enterList")}`}
                            minRows={5}
                            maxRows={10}
                            value={vinValue}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        className={classes.downloadAssetContainer}
                    >
                        <DownloadAsset />
                    </Grid>
                    {vinValue.length > 0 ? (
                        <Grid item xs={12}>
                            <List>
                                {Object.keys(validAssetTypes).map((item) => (
                                    <ListItem
                                        key={item}
                                        className={classes.vinListItem}
                                    >
                                        <ListItemText
                                            secondary={`${strings(item)} - ${
                                                validAssetTypes[item]
                                            }`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Grid>
                    ) : null}
                </Grid>
            )}
        </>
    );
};
